import React, { useEffect, useState } from "react";

const API_BASE_URL = "";

const emptyPackage = {
  name: "",
  experienceType: "",
  route: "",
  duration: "",
  suitability: "",
  image: "",
  imageAlt: "",
  keyInclusions: [],
  shortDesc: "",
  price: "",
  category: "day-cruise",
};

const getMediaUrl = (path) => {
  if (!path) {
    return "";
  }

  if (path.startsWith("http")) {
    return path;
  }

  if (path.startsWith("/uploads/")) {
    return `${API_BASE_URL}${path}`;
  }

  return path;
};

const clonePackage = (pkg) => ({
  ...emptyPackage,
  ...pkg,
  keyInclusions: Array.isArray(pkg?.keyInclusions)
    ? [...pkg.keyInclusions]
    : [],
});

const BrowsePackages = () => {
  const [data, setData] = useState({
    tagline: "",
    heading: "",
    packages: [],
  });
  const [form, setForm] = useState(clonePackage(emptyPackage));
  const [editorIndex, setEditorIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");
  const isEditing = editorIndex !== null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const packagesResponse = await fetch(
          `${API_BASE_URL}/api/browse-packages`
        );

        if (!packagesResponse.ok) {
          throw new Error("Failed to load Browse Packages");
        }

        const packagesResult = await packagesResponse.json();

        setData({
          tagline: packagesResult.tagline || "",
          heading: packagesResult.heading || "",
          packages: packagesResult.packages || [],
        });
      } catch (error) {
        console.error("Browse Packages fetch error:", error);
        alert("Failed to load browse packages data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSectionChange = (field, value) => {
    setData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Image upload failed");
      }

      handleFormChange("image", result.fileUrl);
    } catch (error) {
      console.error("Image upload error:", error);
      alert(error.message || "Failed to upload image");
    }
  };

  const startAdd = () => {
    setEditorIndex(null);
    setForm(clonePackage(emptyPackage));
  };

  const startEdit = (index) => {
    setEditorIndex(index);
    setForm(clonePackage(data.packages[index]));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyFormToPackages = (packages) => {
    const nextPackages = [...packages];

    if (isEditing) {
      nextPackages[editorIndex] = clonePackage(form);
      return nextPackages;
    }

    const hasContent = Boolean(
      form.name || form.image || form.shortDesc || form.price
    );

    if (hasContent) {
      nextPackages.push(clonePackage(form));
    }

    return nextPackages;
  };

  const persist = async (nextData, successMessage) => {
    const response = await fetch(`${API_BASE_URL}/api/browse-packages`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(nextData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Failed to save changes");
    }

    const saved = {
      tagline: result.data.tagline || "",
      heading: result.data.heading || "",
      packages: result.data.packages || [],
    };

    setData(saved);
    alert(successMessage);
    return saved;
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const nextData = {
        ...data,
        packages: applyFormToPackages(data.packages),
      };

      const saved = await persist(
        nextData,
        "Browse Packages updated successfully"
      );

      if (!isEditing) {
        const addedNew = saved.packages.length > data.packages.length;
        if (addedNew) {
          startAdd();
        }
      } else {
        setForm(clonePackage(saved.packages[editorIndex] || emptyPackage));
      }
    } catch (error) {
      console.error("Save error:", error);
      alert(error.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const item = data.packages[index];
    const label = item?.name || `Package ${index + 1}`;

    if (
      !window.confirm(
        `Delete "${label}"? This will remove it from the website.`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const nextPackages = data.packages.filter(
        (_, itemIndex) => itemIndex !== index
      );

      await persist(
        { ...data, packages: nextPackages },
        "Package deleted successfully"
      );

      if (editorIndex === index) {
        startAdd();
      } else if (editorIndex !== null && editorIndex > index) {
        setEditorIndex(editorIndex - 1);
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert(error.message || "Failed to delete package");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Browse Packages</h1>
          <p>Add a new package or update an existing one from the list below.</p>
        </div>

        <button
          type="button"
          className="cms-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="cms-packages-main">
        <div className="cms-section-card">
          <div className="cms-section-card-header">
            <h2>Section Content</h2>
            <p>Edit the heading shown above the package cards.</p>
          </div>

          <div className="cms-form-grid">
            <div className="cms-form-group">
              <label>Tagline</label>
              <input
                type="text"
                value={data.tagline || ""}
                onChange={(e) => handleSectionChange("tagline", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Heading</label>
              <input
                type="text"
                value={data.heading || ""}
                onChange={(e) => handleSectionChange("heading", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="cms-section-card">
          <div className="cms-section-card-header">
            <h2>{isEditing ? "Update Package" : "Add Package"}</h2>
            <p>
              {isEditing
                ? `Editing package ${editorIndex + 1}. Save changes to update it.`
                : "Fill the form and save to add a new package."}
            </p>
          </div>

          <div className="cms-editor-toolbar">
            <button type="button" className="cms-upload-btn" onClick={startAdd}>
              Add New
            </button>
          </div>

          <div className="cms-form-grid">
          <div className="cms-form-group">
            <label>Package Image</label>
            <div className="cms-upload-box">
              <div className="cms-upload-preview">
                {form.image ? (
                  <img
                    src={getMediaUrl(form.image)}
                    alt={form.name || "Package"}
                  />
                ) : (
                  <span>No image selected</span>
                )}
              </div>
              <div className="cms-upload-actions">
                <input
                  type="file"
                  id="browse-package-image-editor"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="cms-file-input"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                />
                <label
                  htmlFor="browse-package-image-editor"
                  className="cms-upload-btn"
                >
                  Choose Image
                </label>
              </div>
            </div>
            <small>JPG, JPEG, PNG or WEBP.</small>
          </div>

          <div className="cms-form-group">
            <label>Title</label>
            <input
              type="text"
              value={form.name || ""}
              onChange={(e) => handleFormChange("name", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Experience Type</label>
            <input
              type="text"
              value={form.experienceType || ""}
              onChange={(e) =>
                handleFormChange("experienceType", e.target.value)
              }
            />
          </div>

          <div className="cms-form-group">
            <label>Route</label>
            <input
              type="text"
              value={form.route || ""}
              onChange={(e) => handleFormChange("route", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Duration</label>
            <input
              type="text"
              value={form.duration || ""}
              onChange={(e) => handleFormChange("duration", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Best For</label>
            <input
              type="text"
              value={form.suitability || ""}
              onChange={(e) => handleFormChange("suitability", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Image Alt Text</label>
            <input
              type="text"
              value={form.imageAlt || ""}
              onChange={(e) => handleFormChange("imageAlt", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Short Description</label>
            <textarea
              rows="4"
              value={form.shortDesc || ""}
              onChange={(e) => handleFormChange("shortDesc", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Key Inclusions</label>
            <textarea
              rows="5"
              value={(form.keyInclusions || []).join("\n")}
              onChange={(e) =>
                handleFormChange(
                  "keyInclusions",
                  e.target.value
                    .split("\n")
                    .map((item) => item.trim())
                    .filter(Boolean)
                )
              }
            />
            <small>One inclusion per line.</small>
          </div>

          <div className="cms-form-group">
            <label>Starting Price</label>
            <input
              type="text"
              value={form.price || ""}
              onChange={(e) => handleFormChange("price", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Category</label>
            <select
              value={form.category || "day-cruise"}
              onChange={(e) => handleFormChange("category", e.target.value)}
            >
              <option value="day-cruise">Coastal Day Trips</option>
              <option value="island">Island Outings</option>
              <option value="luxury">Luxury Sailing</option>
            </select>
          </div>
          </div>

          <div className="cms-bottom-actions">
            <button
              type="button"
              className="cms-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="cms-section-card">
          <div className="cms-section-card-header">
            <h2>Existing Packages</h2>
            <p>Update fills the form above. Delete asks for confirmation first.</p>
          </div>

          {data.packages.length === 0 ? (
            <p className="cms-inquiries-empty">No packages added yet.</p>
          ) : (
            <div className="cms-item-list">
              {data.packages.map((pkg, index) => (
                <div
                  className={`cms-item-row${
                    editorIndex === index ? " is-active" : ""
                  }`}
                  key={index}
                >
                  <div className="cms-item-row-thumb">
                    {pkg.image ? (
                      <img
                        src={getMediaUrl(pkg.image)}
                        alt={pkg.name || "Package"}
                      />
                    ) : (
                      <span>No image</span>
                    )}
                  </div>
                  <div className="cms-item-row-info">
                    <strong>{pkg.name || `Package ${index + 1}`}</strong>
                    <span>{pkg.experienceType || "No type"}</span>
                  </div>
                  <div className="cms-item-row-actions">
                    <button
                      type="button"
                      className="cms-upload-btn"
                      onClick={() => startEdit(index)}
                    >
                      Update
                    </button>
                    <button
                      type="button"
                      className="cms-delete-btn"
                      onClick={() => handleDelete(index)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowsePackages;
